/**
 * MASK HELPERS (Máscaras de Entrada e Exibição)
 * 
 * Centralizamos aqui todas as regex e transformações de strings para o padrão brasileiro.
 */

class MaskHelper {

    /**
     * MÁSCARA DE TELEFONE (Celular/Fixo)
     * (99) 99999-9999 ou (99) 9999-9999
     */
    static phone(value) {
        if (!value) return ""
        value = value.replace(/\D/g, "")
        value = value.replace(/^(\d{2})(\d)/g, "($1) $2")
        if (value.length > 13) {
            value = value.replace(/(\d{5})(\d)/, "$1-$2")
        } else {
            value = value.replace(/(\d{4})(\d)/, "$1-$2")
        }
        return value.substring(0, 15)
    }

    /**
     * MÁSCARA DE CPF
     * 999.999.999-99
     */
    static cpf(value) {
        if (!value) return ""
        value = value.replace(/\D/g, "")
        return value
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d{1,2})/, "$1-$2")
            .replace(/(-\d{2})\d+?$/, "$1")
    }

    /**
     * MÁSCARA DE CNPJ
     * 99.999.999/9999-99
     */
    static cnpj(value) {
        if (!value) return ""
        value = value.replace(/\D/g, "")
        return value
            .replace(/^(\d{2})(\d)/, "$1.$2")
            .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/\.(\d{3})(\d)/, ".$1/$2")
            .replace(/(\d{4})(\d)/, "$1-$2")
            .replace(/(-\d{2})\d+?$/, "$1")
    }

    /**
     * MÁSCARA DE CEP
     * 99999-999
     */
    static cep(value) {
        if (!value) return ""
        value = value.replace(/\D/g, "")
        return value.replace(/(\d{5})(\d)/, "$1-$2").replace(/(-\d{3})\d+?$/, "$1")
    }

    /**
     * MÁSCARA DE DATA (DD/MM/YYYY)
     */
    static date(value) {
        if (!value) return "";
        value = value.replace(/\D/g, "");
        if (value.length > 8) {
            value = value.substring(0, 8);
        }
        if (value.length > 4) {
            return `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4)}`;
        } else if (value.length > 2) {
            return `${value.substring(0, 2)}/${value.substring(2)}`;
        }
        return value;
    }

    /**
     * MÁSCARA DE MOEDA (BRL)
     * 1.250,50
     */
    static currency(value) {
        if (!value && value !== 0) return ""
        let v = typeof value === "number" ? value.toFixed(2) : value
        v = v.replace(/\D/g, "")
        v = (v / 100).toFixed(2) + ""
        v = v.replace(".", ",")
        v = v.replace(/(\d)(\d{3})(\d{3}),/g, "$1.$2.$3,")
        v = v.replace(/(\d)(\d{3}),/g, "$1.$2,")
        return v
    }

    /**
     * LIMPAR MÁSCARA (Apenas números)
     * Útil para enviar ao banco de dados via transformadores de schema.
     */
    static unmask(value) {
        if (!value) return ""
        return value.replace(/\D/g, "")
    }
}

/**
 * ADDRESS HELPERS (Integração de CEP)
 */
class AddressHelper {
    /**
     * Busca endereço completo via API ViaCEP
     * @param {string} cep - CEP formatado ou limpo
     */
    static async fetchByCep(cep) {
        if (!cep) return null;

        const cleanCep = cep.replace(/\D/g, "");
        if (cleanCep.length !== 8) return null;

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
            const data = await response.json();

            if (data.erro) {
                throw new Error("CEP não encontrado.");
            }

            return {
                street: data.logradouro,
                neighborhood: data.bairro,
                city: data.localidade,
                state: data.uf,
                zipCode: data.cep,
                complement: data.complemento || ""
            };
        } catch (error) {
            console.error("Erro ao buscar CEP:", error);
            throw error;
        }
    }
}



/**
 * CARD HELPERS (Lógica de Bandeiras de Cartão)
 */
class CardHelper {
    /**
     * Retorna o nome legível de uma bandeira pelo ID.
     */
    static getFlagName(id, flags = []) {
        if (!id) return "";
        // Tenta encontrar entre as predefinidas
        const flag = flags.find(f => f.id === id);
        if (flag) return flag.name;
        // Bandeira customizada: remove prefixo outra_ (legado) e formata o nome
        const cleanId = id.startsWith('outra_') ? id.slice(6) : id;
        return cleanId
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    /**
     * Retorna o ícone MDI de uma bandeira pelo ID.
     */
    static getFlagIcon(id, flags = []) {
        if (!id) return "mdi mdi-credit-card-outline";
        const flag = flags.find(f => f.id === id);
        return flag ? flag.icon : "mdi mdi-credit-card-outline";
    }
}

/**
 * SEARCH HELPERS (Normalização de texto para busca)
 */
class SearchHelper {
    /**
     * Normaliza texto para busca: remove acentos, espaços extras e converte para lowercase.
     * Usado para gerar campos `searchName` no Firestore.
     * @param {string} value - Texto a ser normalizado
     * @returns {string} Texto normalizado
     */
    static normalize(value) {
        if (!value) return "";
        return value
            .toLowerCase()
            .trim()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, " ");
    }
}

export { MaskHelper, AddressHelper, CardHelper, SearchHelper }
